import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  GraduationCap, Calendar, BookOpen, FileText, Bell, User,
  Clock, CheckCircle, XCircle, AlertCircle, Loader2, LogOut,
  CreditCard, TrendingUp, Home, ClipboardList, Settings, Bus,
  ChevronRight, Award, Target
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentMobileApp = () => {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [student, setStudent] = useState(null);
  
  // Login form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Data states
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [attendance, setAttendance] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState({ present: 0, absent: 0, total: 0 });
  const [homework, setHomework] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [notices, setNotices] = useState([]);
  const [fees, setFees] = useState([]);
  const [results, setResults] = useState([]);

  // Check saved session
  useEffect(() => {
    const savedToken = localStorage.getItem('student_app_token');
    const savedStudent = localStorage.getItem('student_app_data');
    if (savedToken && savedStudent) {
      setToken(savedToken);
      setStudent(JSON.parse(savedStudent));
      setIsLoggedIn(true);
    }
  }, []);

  // Fetch data when logged in
  useEffect(() => {
    if (isLoggedIn && token) {
      fetchAllData();
    }
  }, [isLoggedIn, token]);

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const { token: authToken, user } = res.data;
      
      if (user.role !== 'student') {
        toast.error('This app is for students only');
        return;
      }
      
      setToken(authToken);
      setStudent(user);
      setIsLoggedIn(true);
      
      localStorage.setItem('student_app_token', authToken);
      localStorage.setItem('student_app_data', JSON.stringify(user));
      
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken(null);
    setStudent(null);
    localStorage.removeItem('student_app_token');
    localStorage.removeItem('student_app_data');
    toast.success('Logged out');
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAttendance(),
        fetchHomework(),
        fetchTimetable(),
        fetchNotices(),
        fetchFees(),
        fetchResults()
      ]);
    } catch (error) {
      console.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/attendance/my-attendance`, { headers: getAuthHeader() });
      setAttendance(res.data?.records || []);
      setAttendanceSummary(res.data?.summary || { present: 0, absent: 0, total_days: 0, attendance_percentage: 0 });
    } catch (error) {
      console.error('Attendance fetch error');
    }
  };

  const fetchHomework = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/homework`, { headers: getAuthHeader() });
      setHomework(res.data?.items || res.data || []);
    } catch (error) {
      console.error('Homework fetch error');
    }
  };

  const fetchTimetable = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/timetable/my`, { headers: getAuthHeader() });
      setTimetable(res.data || []);
    } catch (error) {
      console.error('Timetable fetch error');
    }
  };

  const fetchNotices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notices`, { headers: getAuthHeader() });
      setNotices((res.data?.items || res.data || []).slice(0, 10));
    } catch (error) {
      console.error('Notices fetch error');
    }
  };

  const fetchFees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fees/my`, { headers: getAuthHeader() });
      setFees(res.data?.items || res.data || []);
    } catch (error) {
      console.error('Fees fetch error');
    }
  };

  const fetchResults = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/grades/my`, { headers: getAuthHeader() });
      setResults(res.data || []);
    } catch (error) {
      console.error('Results fetch error');
    }
  };

  const getAttendanceIcon = (status) => {
    switch (status) {
      case 'present': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'absent': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'late': return <Clock className="h-4 w-4 text-yellow-500" />;
      default: return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const pendingFees = fees.filter(f => f.status === 'pending' || f.status === 'partial');
  const totalPending = pendingFees.reduce((sum, f) => sum + (f.amount - (f.paid_amount || 0)), 0);
  const pendingHomework = homework.filter(h => !h.submitted && new Date(h.due_date) >= new Date());

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="bg-white/20 p-4 rounded-full inline-block mb-4">
              <GraduationCap className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Student App</h1>
            <p className="text-blue-100 mt-2">Access your academic information</p>
          </div>

          <Card className="shadow-2xl">
            <CardHeader>
              <CardTitle>Student Login</CardTitle>
              <CardDescription>Enter your school credentials</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email / Student ID</Label>
                  <Input
                    id="email"
                    type="text"
                    placeholder="student@school.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loginLoading}>
                  {loginLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Logging in...</>
                  ) : (
                    'Login'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-blue-100 text-sm mt-6">
            Cloud Campus Schools
          </p>
        </motion.div>
      </div>
    );
  }

  // Main App
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 pb-16">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-blue-100">Welcome back,</p>
            <h1 className="text-xl font-bold">{student?.name}</h1>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-white/20">
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex items-center gap-2 text-sm text-blue-100">
          <Badge variant="secondary" className="bg-white/20 text-white">
            {student?.class_name || 'Class'}
          </Badge>
          <span>•</span>
          <span>Roll: {student?.roll_number || 'N/A'}</span>
        </div>
      </div>

      {/* Content Area */}
      <div className="px-4 -mt-12">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Attendance</p>
                    <p className="text-2xl font-bold text-green-600">
                      {attendanceSummary.attendance_percentage || 0}%
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Pending Fees</p>
                    <p className="text-2xl font-bold text-red-600">
                      ₹{totalPending.toLocaleString()}
                    </p>
                  </div>
                  <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tab Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Home Tab */}
          <TabsContent value="home" className="space-y-4 mt-0">
            {/* Pending Homework */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="h-4 w-4" /> Pending Homework
                  </CardTitle>
                  <Badge variant="secondary">{pendingHomework.length}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {pendingHomework.length > 0 ? (
                  <div className="space-y-2">
                    {pendingHomework.slice(0, 3).map((hw, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{hw.title}</p>
                          <p className="text-xs text-muted-foreground">{hw.subject}</p>
                        </div>
                        <Badge variant={new Date(hw.due_date) < new Date() ? 'destructive' : 'outline'} className="text-xs">
                          {new Date(hw.due_date).toLocaleDateString()}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No pending homework!</p>
                )}
              </CardContent>
            </Card>

            {/* Recent Notices */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="h-4 w-4" /> Recent Notices
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notices.length > 0 ? (
                  <div className="space-y-2">
                    {notices.slice(0, 3).map((notice, idx) => (
                      <div key={idx} className="p-2 bg-muted/50 rounded-lg">
                        <p className="font-medium text-sm">{notice.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{notice.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No notices</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Attendance Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span>Attendance Rate</span>
                    <span className="font-bold text-lg">{attendanceSummary.attendance_percentage || 0}%</span>
                  </div>
                  <Progress value={attendanceSummary.attendance_percentage || 0} className="h-2" />
                  
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    <div className="text-center p-2 bg-green-50 rounded-lg">
                      <p className="text-lg font-bold text-green-600">{attendanceSummary.present || 0}</p>
                      <p className="text-xs text-muted-foreground">Present</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded-lg">
                      <p className="text-lg font-bold text-red-600">{attendanceSummary.absent || 0}</p>
                      <p className="text-xs text-muted-foreground">Absent</p>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded-lg">
                      <p className="text-lg font-bold text-blue-600">{attendanceSummary.total_days || 0}</p>
                      <p className="text-xs text-muted-foreground">Total</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recent Attendance</CardTitle>
              </CardHeader>
              <CardContent>
                {attendance.length > 0 ? (
                  <div className="space-y-2">
                    {attendance.slice(0, 10).map((record, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {getAttendanceIcon(record.status)}
                          <span className="text-sm">{new Date(record.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                        </div>
                        <Badge variant={record.status === 'present' ? 'default' : 'destructive'} className="capitalize text-xs">
                          {record.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No attendance records</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Homework Tab */}
          <TabsContent value="homework" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">All Homework</CardTitle>
              </CardHeader>
              <CardContent>
                {homework.length > 0 ? (
                  <div className="space-y-2">
                    {homework.map((hw, idx) => (
                      <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-sm">{hw.title}</p>
                          <Badge variant={hw.submitted ? 'default' : 'outline'} className="text-xs">
                            {hw.submitted ? 'Submitted' : 'Pending'}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{hw.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Due: {new Date(hw.due_date).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No homework assigned</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fees Tab */}
          <TabsContent value="fees" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Fee Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {fees.length > 0 ? (
                  <div className="space-y-2">
                    {fees.map((fee, idx) => (
                      <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-sm">{fee.fee_type || fee.type}</p>
                          <Badge variant={fee.status === 'paid' ? 'default' : 'destructive'} className="text-xs capitalize">
                            {fee.status}
                          </Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Amount</span>
                          <span className="font-medium">₹{(fee.amount || 0).toLocaleString()}</span>
                        </div>
                        {fee.due_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Due: {new Date(fee.due_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No fee records</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Award className="h-4 w-4" /> Exam Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                {results.length > 0 ? (
                  <div className="space-y-2">
                    {results.map((result, idx) => (
                      <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-sm">{result.exam_name || result.subject}</p>
                          <Badge className={result.grade === 'A' || result.grade === 'A+' ? 'bg-green-500' : ''}>
                            {result.grade || `${result.marks}/${result.total_marks}`}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{result.subject}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4 text-sm">No results available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t shadow-lg">
        <div className="flex justify-around py-2">
          {[
            { id: 'home', icon: Home, label: 'Home' },
            { id: 'attendance', icon: Calendar, label: 'Attendance' },
            { id: 'homework', icon: BookOpen, label: 'Homework' },
            { id: 'fees', icon: CreditCard, label: 'Fees' },
            { id: 'results', icon: Award, label: 'Results' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center p-2 ${activeTab === tab.id ? 'text-blue-600' : 'text-muted-foreground'}`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="text-xs mt-1">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StudentMobileApp;
